import { ReactNode } from 'react';
import {
  PROPERTY_QUERY_KEY,
  comparatorOptions,
} from 'modules/predictiveGoals/components/queryBuilder/constants';
import {
  AndJoinerComponent,
  EventComponent,
  UpdatedPropertyComponent,
} from './GoalCriteriaElements';
import { GoalCriteriaContainer } from './Styled';
import { PredictiveAudienceGoal, PredictiveGoalQueryClause } from '../types';
import { ensureString, getEventConditionClauses } from '../utils';
import { TEST_IDS } from '../constants';

/** Check and parse if clause is for a property */
export const parseForUpdatedProperty = (
  orIndex: number,
  clause: PredictiveGoalQueryClause,
  isJoinedOrClause: boolean,
): ReactNode => {
  const propertyComparison = clause.property_comparison;
  const comparatorContext = comparatorOptions[clause.dataType as string].find(
    (option) => option.value === propertyComparison?.comparator,
  );
  return (
    <UpdatedPropertyComponent
      label={ensureString(comparatorContext?.label)}
      name={clause.name}
      userValue={ensureString(propertyComparison?.value)}
      isJoinedOrClause={isJoinedOrClause}
      orIndex={orIndex}
      key={`${clause.name}_${orIndex}`}
      comparator={ensureString(propertyComparison?.comparator)}
      symbol={ensureString(comparatorContext?.Suffix)}
    />
  );
};

/** Helper function for parseReadableCriteriaFromGoal, parses individual clause to readable text */
export const parseUpdatedIndividualClause = (
  orIndex: number,
  clause: PredictiveGoalQueryClause,
  isJoinedOrClause: boolean,
): ReactNode => {
  if (Object.hasOwnProperty.call(clause, PROPERTY_QUERY_KEY)) {
    return parseForUpdatedProperty(orIndex, clause, isJoinedOrClause);
  }

  const maybeConditionClauses = getEventConditionClauses(clause);
  if (maybeConditionClauses.length >= 1) {
    return (
      <EventComponent
        conditions={maybeConditionClauses}
        isJoinedOrClause={isJoinedOrClause}
        name={clause.name}
        orIndex={orIndex}
        key={`${clause.name}_${orIndex}`}
      />
    );
  }

  return null;
};

/** Convert goal query into readable text */
export const parseUpdatedReadableCriteriaFromGoal = (
  goalData: PredictiveAudienceGoal,
): ReactNode[] => {
  const readableCriteria: ReactNode[] = [];
  goalData.goal?.and_clauses?.forEach((andClause, andIndex) => {
    if (andIndex !== 0) {
      readableCriteria.push(<AndJoinerComponent key={`and${andIndex}`} />);
    }
    andClause.or_clauses?.forEach((orClause, orIndex) => {
      const isJoinedOrClause = orIndex !== 0;
      const parsedClause = parseUpdatedIndividualClause(orIndex, orClause, isJoinedOrClause);
      readableCriteria.push(parsedClause);
    });
  });
  return readableCriteria;
};

export const UpdatedGoalCriteria = ({ goal }: { goal: PredictiveAudienceGoal }) => {
  const updatedCriteriaElements = parseUpdatedReadableCriteriaFromGoal(goal);
  return (
    <GoalCriteriaContainer data-test={TEST_IDS.GoalCriteriaContainer}>
      {updatedCriteriaElements.length > 0
        ? updatedCriteriaElements
        : 'Criteria currently cannot display'}
    </GoalCriteriaContainer>
  );
};
